import { Close, UnfoldLess, UnfoldMore } from "@mui/icons-material";
import { Box, css, IconButton, styled, Tooltip, Typography } from "@mui/material";
import { ReactNode, useMemo, useState } from "react";

import { TOOLBAR_COLORS } from "components/toolbars/constants";
import { PanelPickerOverlayContext } from "contexts/panelPickerOverlay";

import { WindowMobileTargetContext } from "../windowMobileTargetContext";
import { PANEL_ANIMATION_DURATION, PANEL_WIDTH, SIDE_PANEL_COLORS } from "./constants";
import { SidePanelFabTargetContext } from "./sidePanelFabTargetContext";

const PanelContainer = styled(Box)(
    () => css`
        display: flex;
        flex-direction: column;
        position: relative;
        width: ${PANEL_WIDTH}px;
        height: 100%;
        background-color: ${SIDE_PANEL_COLORS.BG};
        border-radius: 8px;
        box-shadow: 2px 0px 6px 0px rgba(0, 0, 0, 0.1);
        overflow: hidden;
        pointer-events: auto;
        animation: slideIn ${PANEL_ANIMATION_DURATION}ms ease-out;

        @keyframes slideIn {
            from {
                opacity: 0;
                transform: translateX(20px);
            }
            to {
                opacity: 1;
                transform: translateX(0);
            }
        }
    `,
);

const PanelHeader = styled(Box)(
    () => css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px 16px;
        background-color: ${SIDE_PANEL_COLORS.HEADER_BG};
        min-height: 48px;
        flex-shrink: 0;
    `,
);

const HeaderLeft = styled(Box)`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${SIDE_PANEL_COLORS.TEXT};
`;

const HeaderIcon = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
        width: 20px;
        height: 20px;
        color: ${SIDE_PANEL_COLORS.ICON};
    }
`;

const HeaderActions = styled(Box)`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const SidePanelCloseButton = styled(IconButton)`
    padding: 8px;
    background-color: ${SIDE_PANEL_COLORS.BUTTON_BG};
    border-radius: 8px;
    color: ${SIDE_PANEL_COLORS.ICON};

    &:hover {
        background-color: ${TOOLBAR_COLORS.HOVER_DARK};
    }

    svg {
        width: 20px;
        height: 20px;
    }
`;

const PanelBody = styled(Box)(
    () => css`
        flex: 1;
        min-height: 0;
        overflow: auto;
        background-color: ${SIDE_PANEL_COLORS.BG};
    `,
);

export interface SidePanelProps {
    /** Panel title */
    title: string;
    /** Optional subtitle shown below the title (e.g. selected object name) */
    subtitle?: string;
    /** Optional icon */
    icon?: ReactNode;
    /** Panel content */
    children: ReactNode;
    /** Close handler */
    onClose: () => void;
    /** Collapse toggle handler */
    onToggleCollapse?: () => void;
    /** Whether the panel is in collapsed mode */
    isCollapsed?: boolean;
    /** Optional additional header actions */
    headerActions?: ReactNode;
    /**
     * When true, the internal header (title, close, collapse buttons) is not rendered.
     * Use when the panel content supplies its own header.
     */
    hideHeader?: boolean;
    /** Optional test ID */
    "data-testid"?: string;
}

/**
 * Side Panel Component
 *
 * A docked panel that appears on the right side of the viewport.
 * Similar to Window but with fixed positioning instead of drag-and-drop.
 */
export function SidePanel({
    title,
    subtitle,
    icon,
    children,
    onClose,
    onToggleCollapse,
    isCollapsed,
    headerActions,
    hideHeader = false,
    "data-testid": dataTestId,
}: SidePanelProps) {
    const [pickerOverlay, setPickerOverlay] = useState<ReactNode>(null);
    const overlayContextValue = useMemo(() => ({ setOverlay: setPickerOverlay }), []);
    const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);

    return (
        <PanelPickerOverlayContext.Provider value={overlayContextValue}>
            <WindowMobileTargetContext.Provider value={{ target: "sidePanel", isOpen: true }}>
                <SidePanelFabTargetContext.Provider value={panelEl}>
                    <PanelContainer ref={setPanelEl} data-testid={dataTestId}>
                        {!hideHeader && (
                            <PanelHeader className="panel-header">
                                <HeaderLeft>
                                    {icon && <HeaderIcon>{icon}</HeaderIcon>}
                                    <Box display="flex" flexDirection="column">
                                        <Typography
                                            variant={subtitle ? "caption" : "subtitle1"}
                                            fontWeight={600}
                                            color={subtitle ? "text.secondary" : undefined}
                                        >
                                            {title}
                                        </Typography>
                                        {subtitle && (
                                            <Typography variant="subtitle1" fontWeight={600}>
                                                {subtitle}
                                            </Typography>
                                        )}
                                    </Box>
                                </HeaderLeft>
                                <HeaderActions>
                                    {headerActions}
                                    {onToggleCollapse && (
                                        <Tooltip title={isCollapsed ? "Expand panel" : "Collapse panel"}>
                                            <SidePanelCloseButton onClick={onToggleCollapse} size="small">
                                                {isCollapsed ? <UnfoldMore /> : <UnfoldLess />}
                                            </SidePanelCloseButton>
                                        </Tooltip>
                                    )}
                                    <SidePanelCloseButton onClick={onClose} size="small" title="Close panel">
                                        <Close />
                                    </SidePanelCloseButton>
                                </HeaderActions>
                            </PanelHeader>
                        )}
                        <PanelBody>{children}</PanelBody>
                        {pickerOverlay !== null && (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 10,
                                    bgcolor: "white",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                {pickerOverlay}
                            </Box>
                        )}
                    </PanelContainer>
                </SidePanelFabTargetContext.Provider>
            </WindowMobileTargetContext.Provider>
        </PanelPickerOverlayContext.Provider>
    );
}
